import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    LinearProgress,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Form, Formik } from "formik";
import { DatePicker } from '@mui/x-date-pickers';
import { useDropzone, FileRejection } from 'react-dropzone';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import moment from 'moment';
import { formatUtcToLocal } from '../../helper/utils';
import { canEnterLargeReportFee } from '../../helper/userRoles';
import { StateReportModel } from '../../models/StateReportModel';
import { FormLabel as BootstrapFormLabel } from 'react-bootstrap';
import { loadFileForViewer } from '../../services/api/documentViewerService';
import { StateReportDocumentViewerModal } from './StateReportDocumentViewerModal';
import {
    areFiledNotesRequiredSlotsFilled,
    FILED_NOTES_RECONCILIATION_LIST,
    FILED_NOTES_REPORT_OR_INVOICE,
    getFiledNotesTaxAmountPlaceholder,
    isFiledNotesSlotRequired,
    isFiledNotesTaxAmountSatisfied,
    isNoticeActionType,
    listNoActionReportTypeOptions,
    listScheduleReportTypeOptions,
    NoActionScheduleByType,
    resolveNoActionMapping,
    shouldShowFiledNotesAttachments,
    soleNoActionReportType,
} from "../../helper/noClientActionRequired";

const DOCUMENT_DROPZONE_ACCEPT = {
    'application/pdf': ['.pdf'],
    'image/*': [] as string[],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
};

const EXCEL_DROPZONE_ACCEPT = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

type AttachmentSlot = { id: string; type: string; file: File | null };

function defaultFiledNotesSlots(): AttachmentSlot[] {
    return [
        { id: '1', type: FILED_NOTES_REPORT_OR_INVOICE, file: null },
        { id: '2', type: FILED_NOTES_RECONCILIATION_LIST, file: null },
    ];
}

/** Same dashed dropzone look as File Now / Add-Edit State Report. */
function FiledNotesFileDropzone({
    label,
    acceptedFile,
    onFileDrop,
    onViewFile,
    allowExcel = false,
}: {
    label: string;
    acceptedFile: File | null;
    onFileDrop: (file: File | null) => void;
    onViewFile?: (file: File) => void;
    allowExcel?: boolean;
}) {
    const [isHovered, setIsHovered] = useState(false);

    const onDropRejected = (fileRejections: FileRejection[]) => {
        const first = fileRejections?.[0];
        const errCodes = first?.errors?.map((e) => e.code) || [];
        if (errCodes.includes('file-too-large')) {
            Swal.fire({
                icon: 'error',
                title: 'File too large',
                text: 'Maximum file size 10MB.',
                confirmButtonText: 'OK',
            });
            return;
        }
        if (errCodes.includes('file-invalid-type')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid file type',
                text: allowExcel
                    ? 'Only .pdf, Word, image, and Excel (.xlsx) files are allowed.'
                    : 'Only .pdf, Word, and image files are allowed.',
                confirmButtonText: 'OK',
            });
        }
    };

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024,
        accept: allowExcel
            ? { ...DOCUMENT_DROPZONE_ACCEPT, ...EXCEL_DROPZONE_ACCEPT }
            : DOCUMENT_DROPZONE_ACCEPT,
        onDrop: (files) => {
            const file = files?.[0];
            if (file) onFileDrop(file);
        },
        onDropRejected,
    });

    const style = useMemo(
        () =>
            ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                borderWidth: 2,
                borderRadius: 8,
                borderColor: isDragReject ? '#ff1744' : isDragAccept ? '#00e676' : isFocused ? '#2196f3' : '#B0BEC5',
                borderStyle: 'dashed',
                backgroundColor: '#ECEFF1',
                color: '#546E7A',
                outline: 'none',
                transition: 'border .24s ease-in-out',
                minHeight: '120px',
                cursor: 'pointer',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }) as React.CSSProperties,
        [isFocused, isDragAccept, isDragReject],
    );

    const rootProps = getRootProps({ style });
    const inputProps = getInputProps();
    const handleDropzoneClick = (e: React.MouseEvent) => {
        if (acceptedFile && onViewFile) {
            e.preventDefault();
            e.stopPropagation();
            onViewFile(acceptedFile);
        }
    };
    if (acceptedFile && onViewFile) {
        inputProps.disabled = true;
    }
    const handleBoxClick =
        acceptedFile && onViewFile
            ? handleDropzoneClick
            : (rootProps as { onClick?: (e: React.MouseEvent) => void }).onClick;

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{ position: 'relative', width: '100%' }}
        >
            <Box {...rootProps} onClick={handleBoxClick} sx={acceptedFile && onViewFile ? { cursor: 'pointer' } : {}}>
                <input {...inputProps} />
                {acceptedFile ? (
                    <Box sx={{ textAlign: 'center', width: '100%', px: 1, overflow: 'hidden' }}>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                color: '#1976D2',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={acceptedFile.name}
                        >
                            {acceptedFile.name}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#666' }}>
                            {(acceptedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <p style={{ margin: 0 }}>Click or drag file here</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>{label}</p>
                    </Box>
                )}
            </Box>
            {!acceptedFile && isHovered && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '8px',
                            zIndex: 2,
                            cursor: 'pointer',
                        }}
                        onClick={(rootProps as { onClick?: (e: React.MouseEvent) => void }).onClick}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 3,
                            display: 'flex',
                            cursor: 'pointer',
                        }}
                        onClick={(rootProps as { onClick?: (e: React.MouseEvent) => void }).onClick}
                    >
                        <CloudUploadIcon
                            sx={{
                                color: '#ffffff',
                                fontSize: '32px',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            }}
                        />
                    </Box>
                </>
            )}
            {acceptedFile && isHovered && onViewFile && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '8px',
                            zIndex: 2,
                            cursor: 'pointer',
                        }}
                        onClick={handleDropzoneClick}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 3,
                            display: 'flex',
                            cursor: 'pointer',
                        }}
                        onClick={handleDropzoneClick}
                    >
                        <VisibilityIcon
                            sx={{
                                color: '#ffffff',
                                fontSize: '32px',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
}

export type FiledNotesSubmitData = {
    status: string;
    noteType: string;
    noteDetail: string;
    noteUser: string;
    firstRequestDate: string;
    secondRequestDate: string;
    thirdRequestDate: string;
    suspendedDate: string;
    noteDate?: string;
    policySubStatus: string;
    dateDeclarationFiled: string;
    dateFinalReportSentToClient: string;
    taxAmount: string;
    largeReport: boolean;
    largeReportFee: string;
    /** Named files for No Client Action Required Filed Notes. */
    attachments?: Array<{ file: File; type: string }>;
    /** Selected Notice type. Edits stay on this report; Control Panel mappings are not written. */
    typeOfReport?: string;
    instructions?: string;
    notesToClient?: string;
};

interface Iprops {
    handleClose: () => void;
    open: boolean;
    currentStatus?: string;
    /**
     * Persist notes / NCA complete. May be async.
     * Return `true` (or void) on success so the dialog can close after the API finishes;
     * return `false` to keep the dialog open (e.g. API error already shown).
     */
    handleSubmit: (data: FiledNotesSubmitData) => void | boolean | Promise<void | boolean>;
    selectedRows?: StateReportModel[] | null;
    /** Schedule mapping ActionType; when `no_action`, use the Filed Notes layout. */
    mappingActionType?: string;
    /** From Control Panel Required Attachments on the NCA mapping. */
    reportInvoiceRequired?: boolean;
    reconciliationListRequired?: boolean;
    /** From Control Panel Tax amount Required on the NCA mapping. */
    taxRequired?: boolean;
    /** Premium and zero Control Panel mappings. When set, NCAR requires a type before tax entry. */
    scheduleDataByType?: NoActionScheduleByType | null;
    /** Type already chosen on the Notice select. Empty when Filed Notes must ask. */
    initialTypeOfReport?: string;
    /** Notice of State Report type chosen here — close Filed Notes and resume File Now. */
    onReturnToNotice?: (typeOfReport: string) => void;
}

// Pairs [stateName, name] that should set openFiledNotes true when matched
const FILED_NOTES_STATE_NAME_PAIRS: [string, string][] = [
    ['CO', 'Monthly Report'],
    ['KY', 'Annual Municipal Reconciliation (Electronic)'],
    ['KY', 'Annual Municipal Reconciliation (Individual Municipalities)'],
    ['MD', 'Quarterly Surplus Lines Tax Report'],
    ['MO', 'Quarterly Tax Report (Appendix 1)'],
    ['MO', 'Annual Tax Report (Appendix 3)'],
    ['MS', 'Monthly Certification Report'],
    ['PA', 'Monthly 1620 Report'],
];

const StateReportFilerNotes = ({
    open,
    handleClose,
    handleSubmit,
    selectedRows = [],
    currentStatus,
    mappingActionType,
    reportInvoiceRequired = false,
    reconciliationListRequired = false,
    taxRequired = true,
    scheduleDataByType = null,
    initialTypeOfReport = '',
    onReturnToNotice,
}: Iprops) => {
    const { user } = useAuth();
    const canEditLargeReportFee = canEnterLargeReportFee(user?.userRoles);
    // Status being applied (chosen from the Status menu), NOT the row's existing status.
    // Layout + Submit validation must key off the target status, otherwise changing a
    // completed row (e.g. Filed) to Waiting on Client / No Report Required validates
    // against the old status and the Submit button can never enable.
    const targetStatus = (currentStatus && currentStatus.trim())
        ? currentStatus.trim()
        : (selectedRows?.[0]?.status ?? '');

    // Filed Notes layout: schedule mapping no_action, or legacy state/name pairs.
    const [openFiledNotes, setOpenFiledNotes] = useState(false);
    useEffect(() => {
        const row = selectedRows?.[0];
        if (!row) {
            setOpenFiledNotes(false);
            return;
        }
        const isNoActionMapping =
            String(mappingActionType ?? '').trim().toLowerCase() === 'no_action';
        const rowStateName = (row as any).stateName ?? '';
        const rowName = (row as any).name ?? '';
        const matchesPair = FILED_NOTES_STATE_NAME_PAIRS.some(([s, n]) => s === rowStateName && n === rowName);
        setOpenFiledNotes(isNoActionMapping || matchesPair);
    }, [mappingActionType, selectedRows]);

    // State variables
    const [noteDetail, setNoteDetail] = useState<string>('');
    const [noteDetailFiled, setNoteDetailFiled] = useState<string>('');
    const [taxAmount, setTaxAmount] = useState<string>('');
    const [typeOfReport, setTypeOfReport] = useState('');
    const [instructions, setInstructions] = useState('');
    const [clientMessage, setClientMessage] = useState('');
    const appliedTypeKey = useRef('');
    const [largeReport, setLargeReport] = useState(false);
    const [largeReportFee, setLargeReportFee] = useState<string>('');
    const [noteDate, setNoteDate] = useState<string>('');
    const [firstRequestDate, setFirstRequestDate] = useState('');
    // Reset form state when dialog opens for a new transaction
    useEffect(() => {
        if (open) {
            setLargeReportFee('');
            setLargeReport(false);
            setAttachmentSlots(defaultFiledNotesSlots());
        }
    }, [open]);
    const [secondRequestDate, setSecondRequestDate] = useState('');
    const [thirdRequestDate, setThirdRequestDate] = useState('');
    const [suspendedDate, setSuspendedDate] = useState('');
    const [showNoteDateField, setShowNoteDateField] = useState(false);
    const [dateComplete, setDateComplete] = useState('');
    const [dateCompleteError, setDateCompleteError] = useState('');
    const [attachmentSlots, setAttachmentSlots] = useState<AttachmentSlot[]>(defaultFiledNotesSlots);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerIsBlobUrl, setDocumentViewerIsBlobUrl] = useState(false);
    const [documentViewerLoading, setDocumentViewerLoading] = useState(false);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);

    const showNoActionTypeSelect = openFiledNotes && Boolean(scheduleDataByType);
    const reportTypeOptions = useMemo(
        () =>
            onReturnToNotice
                ? listScheduleReportTypeOptions(scheduleDataByType)
                : listNoActionReportTypeOptions(scheduleDataByType),
        [onReturnToNotice, scheduleDataByType],
    );
    const resolvedMapping = useMemo(
        () => resolveNoActionMapping(scheduleDataByType, typeOfReport),
        [scheduleDataByType, typeOfReport],
    );
    const effectiveReportInvoiceRequired = scheduleDataByType
        ? Boolean(resolvedMapping?.reportInvoiceRequired)
        : reportInvoiceRequired;
    const effectiveReconciliationListRequired = scheduleDataByType
        ? Boolean(resolvedMapping?.reconciliationListRequired)
        : reconciliationListRequired;
    const effectiveTaxRequired = scheduleDataByType
        ? Boolean(resolvedMapping?.taxRequired)
        : taxRequired;
    const taxEntryEnabled = !showNoActionTypeSelect || !!typeOfReport;

    const showAttachments = shouldShowFiledNotesAttachments(openFiledNotes, targetStatus);
    const requiredSlotsFilled = areFiledNotesRequiredSlotsFilled(
        attachmentSlots,
        effectiveReportInvoiceRequired,
        effectiveReconciliationListRequired,
    );

    const initialValuesNote = { noteOption: 'Select One', noteDetail: '' };

    const applyResolvedType = (nextType: string) => {
        const resolved = resolveNoActionMapping(scheduleDataByType, nextType);
        setTypeOfReport(resolved?.typeOfReport ?? '');
        setInstructions(resolved?.instructions ?? '');
        setClientMessage(resolved?.notesToClient ?? '');
        const tax = resolved?.taxAmount ?? '';
        setNoteDetailFiled(tax);
        setTaxAmount(tax);
    };

    // One automatic selection per open: the Notice choice, or the only available type.
    // Both types present and none chosen stays empty so premium is not assumed.
    useEffect(() => {
        if (!open) {
            appliedTypeKey.current = '';
            setTypeOfReport('');
            setInstructions('');
            setClientMessage('');
            setNoteDetailFiled('');
            setTaxAmount('');
            return;
        }
        if (!scheduleDataByType || !openFiledNotes || appliedTypeKey.current) return;
        const next = String(initialTypeOfReport ?? '').trim() || soleNoActionReportType(scheduleDataByType);
        appliedTypeKey.current = next || 'choose';
        if (!next) return;
        applyResolvedType(next);
    }, [open, openFiledNotes, scheduleDataByType, initialTypeOfReport]);

    // Prefill Tax Amount from the row only when Filed Notes is not choosing a report type.
    useEffect(() => {
        if (!open || !openFiledNotes || scheduleDataByType) return;
        const prefill = String(selectedRows?.[0]?.taxAmount ?? '').trim();
        if (prefill !== '') {
            setNoteDetailFiled(prefill);
            setTaxAmount(prefill);
        }
    }, [open, openFiledNotes, scheduleDataByType, selectedRows]);

    // Utility functions
    const shouldDisableDate = (date: Date) => {
        const minDate = new Date('1900-01-01');
        const maxDate = new Date();
        return date < minDate || date > maxDate;
    };

    const handleNoteDetailChangeFiled = (event: any) => {
        const update = event.target.value;
        setNoteDetailFiled(update);
        setTaxAmount(update);
    };

    const handleNote = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
    };

    const handleNoteDetailChange = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
        setShowNoteDateField(update !== '');
        setNoteDate(update === '' ? formatUtcToLocal(new Date().toISOString(), 'MM/DD/YYYY') : noteDate);
    };

    const handleNoteDate = (event: any) => {
        setNoteDate(event);
    };

    // Date handlers
    const handleDateComplete = (val: string | undefined) => {
        if (!val) {
            setDateComplete('');
            setDateCompleteError('');
            return;
        }

        const formattedInputDate = formatUtcToLocal(val, "MM/DD/YYYY");
        const todayInUiTimezone = formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY");

        if (formattedInputDate === "N/A") {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        const inputDate = moment(formattedInputDate, "MM/DD/YYYY", true);
        const todayDate = moment(todayInUiTimezone, "MM/DD/YYYY", true);
        if (!inputDate.isValid() || !todayDate.isValid()) {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        if (inputDate.isAfter(todayDate, 'day')) {
            setDateComplete('');
            setDateCompleteError('Please enter a date up to today');
            return;
        }

        setDateComplete(formattedInputDate);
        setDateCompleteError('');
    };

    const handleFirstRequestChange = (val: string | undefined) => {
        if (val != null) {
            setFirstRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSecondRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSecondRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleThirdRequestChange = (val: string | undefined) => {
        if (val != null) {
            setThirdRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSuspendedRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSuspendedDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    // Reset form on close
    const handleBoxClose = () => {
        setNoteDetail('');
        setFirstRequestDate('');
        setSecondRequestDate('');
        setThirdRequestDate('');
        setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
        setSuspendedDate('');
        setTaxAmount('');
        setNoteDetailFiled('');
        setDateComplete('');
        setDateCompleteError('');
        setLargeReport(false);
        setAttachmentSlots(defaultFiledNotesSlots());
        setIsSubmitting(false);
        handleClose();
    };

    const setAttachmentFile = (index: number, file: File | null) => {
        setAttachmentSlots((prev) => {
            const next = [...prev];
            if (!next[index]) return prev;
            next[index] = { ...next[index], file };
            return next;
        });
    };

    const removeAttachmentSlot = (index: number) => {
        setAttachmentSlots((prev) => {
            const next = [...prev];
            if (!next[index]) return prev;
            next[index] = { ...next[index], file: null };
            return next;
        });
    };

    const addAttachmentSlot = () => {
        setAttachmentSlots((prev) => {
            if (prev.length >= 12) return prev;
            const supplementalCount = prev.filter((slot) =>
                slot.type.startsWith('Supplemental Attachment'),
            ).length;
            return [
                ...prev,
                {
                    id: String(Date.now()),
                    type: `Supplemental Attachment ${supplementalCount + 1}`,
                    file: null,
                },
            ];
        });
    };

    const handleViewFile = async (file: File) => {
        if (!user?.token) {
            Swal.fire({
                title: 'Error',
                text: 'You must be signed in to preview documents.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }
        setDocumentViewerOpen(true);
        setDocumentViewerFileName(file.name);
        setDocumentViewerUrl(null);
        setDocumentViewerIsBlobUrl(false);
        setDocumentViewerLoading(true);
        try {
            const result = await loadFileForViewer(file, user.token);
            setDocumentViewerUrl(result.viewerUrl);
            setDocumentViewerIsBlobUrl(result.isBlobUrl);
        } catch (error) {
            console.error('Error loading file for preview:', error);
            setDocumentViewerOpen(false);
            Swal.fire({
                title: 'Preview unavailable',
                text:
                    error instanceof Error
                        ? error.message
                        : 'Unable to preview this file. Please download it instead.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
        } finally {
            setDocumentViewerLoading(false);
        }
    };

    const handleCloseDocumentViewer = () => {
        if (documentViewerUrl && documentViewerIsBlobUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerIsBlobUrl(false);
        setDocumentViewerFileName(null);
        setDocumentViewerLoading(false);
    };

    // Reset form when selectedRows changes
    useEffect(() => {
        if (!selectedRows || selectedRows.length !== 1) {
            setNoteDetail('');
            setFirstRequestDate('');
            setSecondRequestDate('');
            setThirdRequestDate('');
            setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
            setSuspendedDate('');
        }
    }, [selectedRows]);

    return (
        <>
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (isSubmitting) return;
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    handleBoxClose();
                    return;
                }
                handleBoxClose();
            }}
            maxWidth={'lg'}
        >
            <DialogTitle className='card-title'>
                {(openFiledNotes) ? "Filed Notes" : "Filer Notes"}
            </DialogTitle>
            <React.Fragment>
                <Formik
                    initialValues={initialValuesNote}
                    onSubmit={async () => {
                        if (isSubmitting) return;
                        if (!requiredSlotsFilled) return;
                        setIsSubmitting(true);
                        try {
                            const result = await Promise.resolve(
                                handleSubmit({
                                    status: currentStatus ?? '',
                                    noteDetail: noteDetail,
                                    noteType: 'Select One',
                                    noteUser: user?.userFirstName ?? '',
                                    firstRequestDate: firstRequestDate !== "Invalid date" ? firstRequestDate : '',
                                    secondRequestDate,
                                    thirdRequestDate,
                                    suspendedDate,
                                    noteDate: noteDate !== "Invalid date" ? noteDate : '',
                                    policySubStatus: '',
                                    dateDeclarationFiled: '',
                                    dateFinalReportSentToClient: dateComplete ?? '',
                                    taxAmount: taxAmount ?? '',
                                    typeOfReport: typeOfReport ?? '',
                                    instructions: instructions ?? '',
                                    notesToClient: clientMessage ?? '',
                                    largeReport: largeReport,
                                    largeReportFee: canEditLargeReportFee
                                        ? largeReportFee ?? ''
                                        : String(selectedRows?.[0]?.largeReportFee ?? ''),
                                    attachments: showAttachments
                                        ? attachmentSlots
                                              .filter((slot): slot is AttachmentSlot & { file: File } => slot.file != null)
                                              .map((slot) => ({ file: slot.file, type: slot.type }))
                                        : [],
                                }),
                            );
                            // Keep dialog open when parent explicitly reports failure.
                            if (result === false) {
                                return;
                            }
                            handleBoxClose();
                        } catch {
                            // Parent already showed error Swal; keep dialog open.
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    {({ values }) => {
                        return (
                            <Form className='main-form'>
                                {isSubmitting && (
                                    <LinearProgress color="info" sx={{ backgroundColor: '#132D3D' }} />
                                )}
                                <DialogContent>
                                    {(targetStatus === "No Report Required" || openFiledNotes) ? (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            disableFuture
                                                            minDate={new Date('1900-01-01')}
                                                            maxDate={new Date()}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Date Completed"
                                                            className="select-input-border text-input-box"
                                                            value={dateComplete ?? ''}
                                                            onChange={(v) => handleDateComplete(v?.toString())}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="dateComplete"
                                                                    name="dateComplete"
                                                                    error={!!dateCompleteError || !dateComplete}
                                                                    helperText={
                                                                        dateCompleteError
                                                                            ? dateCompleteError
                                                                            : !dateComplete
                                                                            ? 'Date Completed is required'
                                                                            : ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {(openFiledNotes) && (
                                                <>
                                                    {showNoActionTypeSelect && (
                                                        <Grid item xs={12}>
                                                            <FormControl
                                                                fullWidth
                                                                error={!typeOfReport}
                                                                sx={{ minWidth: '400px', marginTop: 1 }}
                                                            >
                                                                <FormLabel required>Type of Report</FormLabel>
                                                                <Select
                                                                    value={typeOfReport}
                                                                    displayEmpty
                                                                    onChange={(event: SelectChangeEvent) => {
                                                                        const nextType = event.target.value;
                                                                        if (
                                                                            nextType
                                                                            && isNoticeActionType(scheduleDataByType, nextType)
                                                                            && onReturnToNotice
                                                                        ) {
                                                                            onReturnToNotice(nextType);
                                                                            return;
                                                                        }
                                                                        applyResolvedType(nextType);
                                                                    }}
                                                                    sx={{ marginTop: '8px' }}
                                                                >
                                                                    <MenuItem value="">Choose an item.</MenuItem>
                                                                    {reportTypeOptions.map((option) => (
                                                                        <MenuItem key={option} value={option}>
                                                                            {option}
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                                <FormHelperText error={!typeOfReport}>
                                                                    {!typeOfReport ? 'Type of Report is required' : ''}
                                                                </FormHelperText>
                                                            </FormControl>
                                                        </Grid>
                                                    )}
                                                    {/* Tax Amount Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                            <TextField
                                                                label="Tax Amount"
                                                                placeholder={getFiledNotesTaxAmountPlaceholder(typeOfReport)}
                                                                type="number"
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{ marginTop: 2 }}
                                                                value={noteDetailFiled}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (/^\d*\.?\d*$/.test(value)) {
                                                                        handleNoteDetailChangeFiled(e);
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    inputProps: { min: 0, step: "0.01" }
                                                                }}
                                                                required={effectiveTaxRequired}
                                                                disabled={!taxEntryEnabled}
                                                                error={effectiveTaxRequired && taxEntryEnabled && !noteDetailFiled && openFiledNotes}
                                                                helperText={
                                                                    effectiveTaxRequired && taxEntryEnabled && !noteDetailFiled && openFiledNotes
                                                                        ? 'This field is required'
                                                                        : ''
                                                                }
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                            <BootstrapFormLabel>Large Report</BootstrapFormLabel>
                                                            <RadioGroup
                                                                row
                                                                value={largeReport}
                                                                onChange={(e) => {
                                                                    setLargeReport(e.target.value === 'true');
                                                                    // Clear the fee when setting largeReport to false
                                                                    if (e.target.value === 'false') {
                                                                        setLargeReportFee('');
                                                                    }
                                                                }}
                                                            >
                                                                <FormControlLabel value="true" control={<Radio />} label="True" />
                                                                <FormControlLabel value="false" control={<Radio />} label="False" />
                                                            </RadioGroup>
                                                            <FormHelperText error={largeReport === null && (openFiledNotes)}>
                                                                {largeReport === null && (openFiledNotes) ? 'This field is required' : ''}
                                                            </FormHelperText>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Fee — Super Admin only */}
                                                    {largeReport && canEditLargeReportFee && (
                                                        <Grid item xs={12}>
                                                            <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                                <TextField
                                                                    label="Large Report Fee"
                                                                    type="number"
                                                                    value={largeReportFee}
                                                                    onChange={(e) => setLargeReportFee(e.target.value)}
                                                                    placeholder="Enter fee amount (e.g., 50.00)"
                                                                    variant="outlined"
                                                                    fullWidth
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}

                                                    {showNoActionTypeSelect && (
                                                        <>
                                                            <Grid item xs={12}>
                                                                <FormControl fullWidth>
                                                                    <FormLabel required={Boolean(resolvedMapping?.instructionsRequired)}>
                                                                        Instructions
                                                                        {resolvedMapping?.instructionsRequired ? '' : ' (Optional)'}
                                                                    </FormLabel>
                                                                    <TextField
                                                                        value={instructions}
                                                                        onChange={(event) => setInstructions(event.target.value)}
                                                                        placeholder={
                                                                            typeOfReport
                                                                                ? 'Click or tap here to enter text.'
                                                                                : 'Select Type of Report to view Instructions'
                                                                        }
                                                                        disabled={!typeOfReport}
                                                                        multiline
                                                                        rows={4}
                                                                        sx={{ marginTop: '8px' }}
                                                                    />
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <FormControl fullWidth>
                                                                    <FormLabel required={Boolean(resolvedMapping?.additionalNotesRequired)}>
                                                                        Additional Notes
                                                                        {resolvedMapping?.additionalNotesRequired ? '' : ' (Optional)'}
                                                                    </FormLabel>
                                                                    <TextField
                                                                        value={clientMessage}
                                                                        onChange={(event) => setClientMessage(event.target.value)}
                                                                        placeholder={
                                                                            typeOfReport
                                                                                ? 'Click or tap here to enter text.'
                                                                                : 'Select Type of Report to view Additional Notes'
                                                                        }
                                                                        disabled={!typeOfReport}
                                                                        multiline
                                                                        rows={4}
                                                                        sx={{ marginTop: '8px' }}
                                                                    />
                                                                </FormControl>
                                                            </Grid>
                                                        </>
                                                    )}

                                                    {showAttachments && (
                                                        <Grid item xs={12}>
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                                                    Attachments
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', mb: 1.5 }}>
                                                                    Please select a box to choose a file from your computer, or
                                                                    drag and drop your file into the appropriate box below.
                                                                    Attach a copy of the completed report or related documents for the client record.
                                                                </Typography>
                                                                <Grid container spacing={2}>
                                                                    {attachmentSlots.map((slot, index) => {
                                                                        const slotRequired = isFiledNotesSlotRequired(
                                                                            slot.type,
                                                                            effectiveReportInvoiceRequired,
                                                                            effectiveReconciliationListRequired,
                                                                        );
                                                                        return (
                                                                        <Grid item xs={12} sm={6} key={slot.id}>
                                                                            <Box sx={{ mb: 0.5, fontWeight: 'medium' }}>
                                                                                {slot.type}
                                                                                {slotRequired && (
                                                                                    <Box component="span" sx={{ color: 'error.main' }}> *</Box>
                                                                                )}
                                                                            </Box>
                                                                            <FiledNotesFileDropzone
                                                                                label={slot.type}
                                                                                acceptedFile={slot.file}
                                                                                onFileDrop={(file) => setAttachmentFile(index, file)}
                                                                                onViewFile={slot.file ? handleViewFile : undefined}
                                                                                allowExcel={slot.type === FILED_NOTES_RECONCILIATION_LIST}
                                                                            />
                                                                            {slot.file && (
                                                                                <Box sx={{ mt: 1, textAlign: 'center' }}>
                                                                                    <Link
                                                                                        component="button"
                                                                                        variant="body2"
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            removeAttachmentSlot(index);
                                                                                        }}
                                                                                        sx={{ color: '#d32f2f', cursor: 'pointer' }}
                                                                                    >
                                                                                        Remove
                                                                                    </Link>
                                                                                </Box>
                                                                            )}
                                                                        </Grid>
                                                                        );
                                                                    })}
                                                                </Grid>
                                                                {attachmentSlots.length < 12 && (
                                                                    <Box sx={{ mt: 2 }}>
                                                                        <Button
                                                                            variant="outlined"
                                                                            type="button"
                                                                            startIcon={<AttachFileIcon />}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                addAttachmentSlot();
                                                                            }}
                                                                            sx={{
                                                                                textTransform: 'none',
                                                                                fontWeight: 600,
                                                                                borderColor: '#1976D2',
                                                                                color: '#1976D2',
                                                                                '&:hover': {
                                                                                    borderColor: '#1565C0',
                                                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            Add More Supplemental Attachments
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </>
                                            )}

                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={handleNote}
                                                        required={targetStatus === "No Report Required"}
                                                        error={!noteDetail && targetStatus === "No Report Required"}
                                                        helperText={
                                                            !noteDetail && targetStatus === "No Report Required"
                                                                ? 'Note is required'
                                                                : ''
                                                        }
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '500px' }}>
                                                    <FormHelperText className='formtexthelper-heading'>
                                                        Note
                                                    </FormHelperText>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={(e) => handleNoteDetailChange(e)}
                                                        required
                                                        error={!noteDetail.trim()}
                                                        helperText={!noteDetail.trim() ? 'Note is required' : ''}
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sx={{ marginTop: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="First Request"
                                                            className="select-input-border text-input-box"
                                                            value={firstRequestDate ?? ''}
                                                            onChange={(v) => handleFirstRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="firstRequestDate"
                                                                    name="firstRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Second Request"
                                                            className="select-input-border text-input-box"
                                                            value={secondRequestDate ?? ''}
                                                            onChange={(v) => handleSecondRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="secondRequestDate"
                                                                    name="secondRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Third & Final Request"
                                                            className="select-input-border text-input-box"
                                                            value={thirdRequestDate ?? ''}
                                                            onChange={(v) => handleThirdRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="thirdRequestDate"
                                                                    name="thirdRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={.3}></Grid>
                                                {showNoteDateField && (
                                                    <Grid item xl={3} lg={3} md={6} sm={12} xs={12} marginTop={3}>
                                                        <FormHelperText className='formtexthelper-heading'>Note Date</FormHelperText>
                                                        <DatePicker
                                                            inputFormat="MM/DD/YYYY"
                                                            label={null}
                                                            className="select-input-border text-input-box"
                                                            shouldDisableDate={shouldDisableDate}
                                                            value={noteDate}
                                                            onChange={handleNoteDate}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="effectiveDateStart"
                                                                    name="policyEffDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <button
                                        className='btn-2 btn d-flex btn-primary search-btn'
                                        type='button'
                                        onClick={handleBoxClose}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    {(() => {
                                        const hasPermission = user?.userRoles.split(",").includes("Super Admin") ||
                                            user?.userRoles.split(",").includes("MISC Filer") ||
                                            user?.userRoles.split(",").includes("MISC State Reporter");

                                        if (!hasPermission) {
                                            return (
                                                <button
                                                    className='btn btn-primary active-btn-blue search-btn'
                                                    disabled
                                                >
                                                    Submit
                                                </button>
                                            );
                                        }

                                        let isValidForSubmit = false;

                                        if (openFiledNotes) {
                                            const typeChosen = !showNoActionTypeSelect || !!typeOfReport;
                                            const instructionsReady = !showNoActionTypeSelect
                                                || !resolvedMapping?.instructionsRequired
                                                || instructions.trim() !== '';
                                            const clientMessageReady = !showNoActionTypeSelect
                                                || !resolvedMapping?.additionalNotesRequired
                                                || clientMessage.trim() !== '';
                                            isValidForSubmit = !!(
                                                dateComplete
                                                && typeChosen
                                                && instructionsReady
                                                && clientMessageReady
                                                && isFiledNotesTaxAmountSatisfied(noteDetailFiled, effectiveTaxRequired)
                                                && requiredSlotsFilled
                                            );
                                        } else {
                                            switch (targetStatus) {
                                                case "Filed":
                                                    isValidForSubmit = !!(dateComplete && noteDetailFiled);
                                                    break;
                                                case "No Report Required":
                                                    isValidForSubmit = !!(dateComplete && noteDetail.trim());
                                                    break;
                                                case "Waiting on Client":
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                                default:
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                            }
                                        }

                                        return (
                                            <button
                                                className='btn btn-primary active-btn-blue search-btn'
                                                type='submit'
                                                disabled={!isValidForSubmit || isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                                                        Saving…
                                                    </span>
                                                ) : (
                                                    'Submit'
                                                )}
                                            </button>
                                        );
                                    })()}
                                </DialogActions>
                            </Form>
                        );
                    }}
                </Formik>
            </React.Fragment>
        </Dialog>
        <StateReportDocumentViewerModal
            open={documentViewerOpen}
            onClose={handleCloseDocumentViewer}
            fileName={documentViewerFileName}
            loading={documentViewerLoading}
            viewerUrl={documentViewerUrl}
        />
        </>
    );
};

export default StateReportFilerNotes;
